/**
 * KPIConfigurationPage — `/reporting/kpi-config`.
 *
 * Wired to real backend:
 *   GET  /v1/reporting/kpi-definitions — list
 *   POST /v1/reporting/kpi-definitions — create
 *
 * B-slice 5 ships `kpi_definition` table + the endpoints above.
 */
import { type JSX, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Target } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  listKpiDefinitions,
  createKpiDefinition,
  type KpiDefinitionWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

const METRIC_KEYS = [
  'DAILY_REVENUE_BY_REGION',
  'MAIL_VOLUME_TRENDS',
  'CUSTOMER_ACQUISITION_FUNNEL',
  'DRIVER_PERFORMANCE',
  'WALLET_ACTIVITY_SUMMARY',
  'AUDIT_COMPLIANCE_HEATMAP',
  'NIPOST_PAY_ADOPTION',
  'GOVERNMENT_SERVICE_THROUGHPUT',
  'RECEIPT_INVOICE_KPIS',
] as const;

export function KPIConfigurationPage(): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [metricKey, setMetricKey] = useState<string>(METRIC_KEYS[0]);
  const [direction, setDirection] = useState<'HIGHER_BETTER' | 'LOWER_BETTER' | 'RANGE'>('HIGHER_BETTER');
  const [warnThreshold, setWarnThreshold] = useState('');
  const [criticalThreshold, setCriticalThreshold] = useState('');
  const [unit, setUnit] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const kpis = useQuery<readonly KpiDefinitionWire[], Error>({
    queryKey: ['reporting', 'kpi-definitions'],
    queryFn: () => listKpiDefinitions(),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () =>
      createKpiDefinition({
        name: name.trim(),
        metricKey,
        direction,
        warnThreshold: warnThreshold ? Number(warnThreshold) : null,
        criticalThreshold: criticalThreshold ? Number(criticalThreshold) : null,
        unit: unit.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reporting', 'kpi-definitions'] });
      void qc.invalidateQueries({ queryKey: ['reporting', 'dashboard'] });
      setName('');
      setWarnThreshold('');
      setCriticalThreshold('');
      setUnit('');
      setFormError(null);
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'Failed to create KPI');
    },
  });

  const denied =
    kpis.isError &&
    kpis.error instanceof ApiError &&
    (kpis.error.status === 401 || kpis.error.status === 403);

  function submit(): void {
    if (!name.trim()) {
      setFormError('Name is required');
      return;
    }
    mutation.mutate();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="KPI configuration"
        description="Define KPI rules backed by the reporting data pipeline."
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/manageKpiDefinitions</code> required.
        </DeferredNotice>
      )}

      {!denied && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-nipost-green" />
            <h3 className="font-semibold text-gray-900">Add KPI definition</h3>
          </div>
          {formError && (
            <p className="text-xs text-red-600 mb-3">{formError}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Daily Revenue Target"
                aria-label="KPI name"
                data-testid="kpi-name-input"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Metric key</label>
              <select
                value={metricKey}
                onChange={(e) => setMetricKey(e.target.value)}
                aria-label="Metric key"
                data-testid="kpi-metric-select"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              >
                {METRIC_KEYS.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Direction</label>
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value as typeof direction)}
                aria-label="Direction"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              >
                <option value="HIGHER_BETTER">Higher is better</option>
                <option value="LOWER_BETTER">Lower is better</option>
                <option value="RANGE">Range</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Warn threshold</label>
              <input
                type="number"
                value={warnThreshold}
                onChange={(e) => setWarnThreshold(e.target.value)}
                placeholder="Optional"
                aria-label="Warn threshold"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Critical threshold</label>
              <input
                type="number"
                value={criticalThreshold}
                onChange={(e) => setCriticalThreshold(e.target.value)}
                placeholder="Optional"
                aria-label="Critical threshold"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Unit</label>
              <input
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="e.g. NGN, %, count"
                aria-label="Unit"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={mutation.isPending}
            data-testid="kpi-save-btn"
            className="mt-4 inline-flex items-center gap-1 px-4 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            {mutation.isPending ? 'Saving…' : 'Save KPI'}
          </button>
        </div>
      )}

      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">
          KPI definitions ({kpis.data?.length ?? 0})
        </h3>
        {kpis.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : kpis.isError && !denied ? (
          <p className="text-sm text-red-600">{kpis.error.message}</p>
        ) : kpis.data && kpis.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Metric</th>
                  <th className="text-left px-3 py-2">Direction</th>
                  <th className="text-right px-3 py-2">Warn</th>
                  <th className="text-right px-3 py-2">Critical</th>
                  <th className="text-left px-3 py-2">Unit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {kpis.data.map((k) => (
                  <tr key={k.id} data-testid="kpi-row">
                    <td className="px-3 py-2 font-medium text-gray-900">{k.name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{k.metricKey}</td>
                    <td className="px-3 py-2 text-xs">{k.direction}</td>
                    <td className="px-3 py-2 text-right text-xs">{k.warnThreshold ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-xs">{k.criticalThreshold ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{k.unit || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted">No KPI definitions yet.</p>
        )}
      </div>
    </div>
  );
}
